import type { Character, TournamentDetail, TournamentMatch } from '@drawgon/shared';

/**
 * トーナメント表の組み立て（#151）。
 *
 * **純粋関数だけを置く。** 画面を描かずにテストできるようにするため。
 * 大会そのものの決まりは `docs/specs/tournament.md`、判断は `docs/adr/023-tournament-progression.md`。
 */

export interface BracketSide {
  character?: Character;
  /** この側が次へ進んだか。決着していなければ `false`。 */
  isWinner: boolean;
}

export interface BracketMatch {
  id: string;
  a: BracketSide;
  b: BracketSide;
  /** 決着しているか。**枠は戦う前から存在する**（ADR-023）ので、空と未決着は別物。 */
  isSettled: boolean;
  scheduledAt: string;
}

export interface BracketRound {
  round: number;
  matches: BracketMatch[];
}

/**
 * ラウンドの名前。**「何回戦か」ではなく「決勝まであと何回か」で決まる。**
 *
 * 8 人なら 1回戦 / 準決勝 / 決勝、16 人なら 1回戦 / 2回戦 / 準決勝 / 決勝。
 * iOS の `TournamentLabels.round` と同じ規則（言語が違うので実装は分かれる）。
 */
export function roundName(round: number, totalRounds: number): string {
  switch (totalRounds - round) {
    case 0:
      return '決勝';
    case 1:
      return '準決勝';
    default:
      return `${round}回戦`;
  }
}

/** ラウンドごとにまとめる。枠の順（`position`）は保つ。 */
export function roundsOf(detail: TournamentDetail): BracketRound[] {
  const byId = new Map(detail.characters.map((character) => [character.id, character]));
  const side = (id: string | undefined, match: TournamentMatch): BracketSide => ({
    character: id ? byId.get(id) : undefined,
    // **`winnerId` が無いうちは誰も勝者ではない。** 「片方だけ埋まっている＝勝ち」ではない。
    isWinner: match.winnerId !== undefined && match.winnerId === id,
  });

  const rounds: BracketRound[] = [];
  for (let round = 1; round <= detail.totalRounds; round += 1) {
    const matches = detail.matches
      .filter((match) => match.round === round)
      .sort((a, b) => a.position - b.position)
      .map((match) => ({
        id: match.id,
        a: side(match.characterAId, match),
        b: side(match.characterBId, match),
        isSettled: match.winnerId !== undefined,
        scheduledAt: match.scheduledAt,
      }));

    rounds.push({ round, matches });
  }

  return rounds;
}

/** 優勝した選手。決まっていなければ `undefined`。 */
export function championOf(detail: TournamentDetail): Character | undefined {
  const final = detail.matches.find((match) => match.round === detail.totalRounds);
  if (!final?.winnerId) return undefined;

  return detail.characters.find((character) => character.id === final.winnerId);
}
